import type { LucideIcon } from 'lucide-react';

import { HistoriaNaMesa } from '@/historias/mesa';

/**
 * Card de indicador (v2.1, p.25). Um indicador responde a uma pergunta que o
 * gestor faz de manha. As partes, de cima para baixo:
 *
 *  - BLOCO DE ICONE: 36 px, raio 12, na tinta do tom do assunto (p.13), com o
 *    icone de 18 px na cor 500 — ao lado do ROTULO, de 13 px no secundario,
 *    que diz o que e medido sem abreviar;
 *  - VALOR: Sora 28, algarismos tabulares, PRETO. Fica vermelho so quando o
 *    proprio valor e o alerta ("Manutencao vencida 6"): `alerta` o pinta no
 *    vermelho de parada. Nenhum outro caso;
 *  - VARIACAO: seta e cor pela direcao boa ou ruim, sempre com a base ("vs.
 *    ontem"), num selo na tinta do tom. Quem diz o sentido e a tela, pela prop
 *    `sentido`: para custo e tempo parado, subir e piorar;
 *  - CONTEXTO: proporcao, base ou periodo em 12 px. Nunca a definicao;
 *  - BARRA DE PROPORCAO: 6 px, na cor do tom, so quando o valor e parte de um
 *    todo ("67% da frota").
 *
 * O VALOR E TEXTO, E A UNIDADE E DESENHADA AQUI
 * O card NAO aceita no pronto no lugar do valor. Qualquer filho com utilitario
 * de fonte — `Medida`, por exemplo, que traz `numero` — vence a fonte herdada
 * do `kpi-valor`, e o numero sai em 14 px no lugar de Sora 28. Na mesma faixa
 * isso aparece como valores de tamanhos diferentes lado a lado, que e
 * exatamente o defeito que o `grid-rows-subgrid` da `FaixaIndicadores` existe
 * para evitar.
 *
 * CARD INTEIRO CLICAVEL
 * Se o card leva a algum lugar, e o card todo que clica — nao um link escondido
 * no canto. Clicavel, ele sobe para a elevacao 2 no hover (p.25) e abre a
 * lista ja filtrada. Sem laranja, que e da acao primaria da tela.
 */

const BASE =
  'cartao grid grid-cols-[auto_minmax(0,1fr)] grid-rows-subgrid row-span-4 gap-x-3 gap-y-2 p-4 text-left';

/** O tom do assunto (p.13): bloco na tinta, icone e barra na cor 500. Literais para o Tailwind ler. */
export type TomDoIndicador =
  | 'marca'
  | 'operando'
  | 'standby'
  | 'parada'
  | 'manutencao'
  | 'disponivel'
  | 'deslocamento'
  | 'neutro';

const TONS: Record<TomDoIndicador, { readonly bloco: string; readonly icone: string; readonly barra: string }> = {
  marca: { bloco: 'bg-marca-fundo', icone: 'text-marca', barra: 'bg-marca' },
  operando: { bloco: 'bg-operando-fundo', icone: 'text-operando', barra: 'bg-operando' },
  standby: { bloco: 'bg-standby-fundo', icone: 'text-standby', barra: 'bg-standby' },
  parada: { bloco: 'bg-parada-fundo', icone: 'text-parada', barra: 'bg-parada' },
  manutencao: { bloco: 'bg-manutencao-fundo', icone: 'text-manutencao', barra: 'bg-manutencao' },
  disponivel: { bloco: 'bg-disponivel-fundo', icone: 'text-disponivel', barra: 'bg-disponivel' },
  deslocamento: { bloco: 'bg-deslocamento-fundo', icone: 'text-deslocamento', barra: 'bg-deslocamento' },
  neutro: { bloco: 'bg-nao-alocado-fundo', icone: 'text-nao-alocado', barra: 'bg-nao-alocado' },
};

export function CardIndicador({
  rotulo,
  valor,
  unidade,
  unidadeColada = false,
  contexto,
  variacao,
  sentido,
  icone: Icone,
  tom = 'neutro',
  alerta = false,
  proporcao,
  aoAbrir,
  carregando = false,
  historia,
}: {
  readonly rotulo: string;
  /** Ja formatado (`formatarInteiro`, `formatarReais`). Texto, nunca no pronto. */
  readonly valor: string;
  /** `L`, `L/h`, `/h`. O card desenha a unidade em cinza, separada do valor. */
  readonly unidade?: string;
  /** `true` para sufixos que nao levam espaco, como o `/h` de `R$ 320,50/h`. */
  readonly unidadeColada?: boolean;
  readonly contexto: string;
  /** So com periodo de comparacao real. Ex.: "+3 vs. semana passada". */
  readonly variacao?: string;
  /** Se a variacao e boa ou ruim. Sem ele, a variacao sai neutra e sem seta. */
  readonly sentido?: 'melhorou' | 'piorou';
  /** Icone Lucide do vocabulario normativo (skill `style`). */
  readonly icone: LucideIcon;
  readonly tom?: TomDoIndicador;
  /** O proprio valor e o alerta: numero em vermelho (p.25). */
  readonly alerta?: boolean;
  /** Fracao de 0 a 1: desenha a barra de proporcao na cor do tom. */
  readonly proporcao?: number;
  /** Quando existe, o CARD INTEIRO vira o clique. */
  readonly aoAbrir?: () => void;
  /**
   * A consulta ainda nao respondeu (p.32): o bloco e o rotulo ficam — eles nao
   * dependem do dado —, e o valor e o contexto viram esqueleto. Nunca "—" nem
   * zero no lugar do numero: zero e um valor, e o "—" parece um.
   */
  readonly carregando?: boolean;
  /**
   * O codigo do modo historias (manual v2.1, p.44 a p.47): o card inteiro e o
   * elemento. O involucro repete o subgrid das quatro linhas, para o card
   * continuar alinhado com os vizinhos da `FaixaIndicadores`.
   */
  readonly historia?: string;
}) {
  const cores = TONS[tom];
  const seta = sentido === undefined ? '' : variacaoSobe(variacao) ? '↗ ' : '↘ ';
  const corDaVariacao =
    sentido === 'melhorou'
      ? 'bg-operando-fundo text-variacao-melhor'
      : sentido === 'piorou'
        ? 'bg-parada-fundo text-variacao-pior'
        : 'bg-tela text-tinta-secundaria';
  const conteudo = (
    <>
      <span
        aria-hidden="true"
        className={`flex size-[var(--indicador-bloco)] items-center justify-center rounded-controle ${cores.bloco}`}
      >
        <Icone className={`size-[var(--indicador-icone)] ${cores.icone}`} />
      </span>
      <span className="kpi-rotulo self-center text-pretty">{rotulo}</span>
      {carregando ? (
        <>
          {/* `kpi-valor` fica: a FaixaIndicadores mede o card por ele. As
              caixas tem a ALTURA DA LINHA de verdade — 32 px do valor (Sora
              28/32) e 16 do contexto (12/16) —, para o card nao crescer nem
              encolher quando o numero chega. */}
          <span className="col-span-2 flex items-center">
            <span className="kpi-valor">
              <span aria-hidden="true" className="flex h-8 items-center">
                <span className="esqueleto block h-6 w-24" />
              </span>
              <span className="sr-only">carregando</span>
            </span>
          </span>
          {/* O contexto que nao depende do dado ("o posto cobrou diferente do
              que foi lançado") ja se sabe, e aparece; o que depende vem vazio
              de quem chama, e vira barra. */}
          {contexto ? (
            <span className="kpi-contexto col-span-2 text-pretty">{contexto}</span>
          ) : (
            <span aria-hidden="true" className="col-span-2 flex h-4 items-center">
              <span className="esqueleto block h-3 w-4/5" />
            </span>
          )}
          <span className="col-span-2" />
        </>
      ) : (
        <>
          {/* O valor NAO QUEBRA e nao e cortado: e o dado principal do card. Quem
              garante espaco e a FaixaIndicadores, que cai de coluna antes. */}
          <span className="col-span-2 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`whitespace-nowrap ${alerta ? 'kpi-valor text-parada' : 'kpi-valor'}`}>
              {valor}
              {unidade ? (
                <span className="txt-apoio text-tinta-apagada">
                  {unidadeColada ? '' : ' '}
                  {unidade}
                </span>
              ) : null}
            </span>
            {variacao ? (
              <span className={`txt-selo inline-flex h-[var(--selo-altura)] items-center rounded-pill px-2 ${corDaVariacao}`}>
                {seta + variacao}
              </span>
            ) : null}
          </span>
          {/* `text-pretty`: o contexto que vai a duas linhas nao deixa uma palavra
              sozinha na segunda. */}
          <span className="kpi-contexto col-span-2 text-pretty">{contexto}</span>
          {proporcao === undefined ? (
            <span className="col-span-2" />
          ) : (
            <span aria-hidden="true" className="col-span-2 block h-[var(--indicador-barra)] overflow-hidden rounded-pill bg-borda-sutil">
              <span
                className={`block h-full rounded-pill ${cores.barra}`}
                style={{ width: `${Math.round(Math.min(Math.max(proporcao, 0), 1) * 100)}%` }}
              />
            </span>
          )}
        </>
      )}
    </>
  );

  const card = !aoAbrir ? (
    <div className={BASE}>{conteudo}</div>
  ) : (
    /* Botao de verdade: recebe foco, responde a Enter e a espaco, e o anel de
       foco global (p.20) vale sem nada a mais. Um `div` com `onClick` nao faz
       nenhuma das tres coisas. */
    <button type="button" onClick={aoAbrir} className={`${BASE} cartao-clicavel cursor-pointer`}>
      {conteudo}
    </button>
  );

  if (!historia) return card;
  return (
    <HistoriaNaMesa codigo={historia} className="row-span-4 grid grid-cols-[minmax(0,1fr)] grid-rows-subgrid">
      {card}
    </HistoriaNaMesa>
  );
}

/** A seta segue o sinal escrito na variacao: "-3%" desce, o resto sobe. */
function variacaoSobe(variacao: string | undefined): boolean {
  return !(variacao ?? '').trim().startsWith('-') && !(variacao ?? '').trim().startsWith('−');
}
